import type {
  TransitionPresentation,
  TransitionPresentationComponentProps,
} from "@remotion/transitions";
import type React from "react";
import { AbsoluteFill, Easing, interpolate } from "remotion";

import { radialTransforms, StackedBlur } from "./stacked-blur.tsx";
import { isTransitionIdle } from "./transition-idle.ts";
import { useStackedBlur, useTransitionBlur } from "./use-transition-blur.ts";

export type ZoomBlurProps = {
  blur?: number;
  rise?: number;
  zoom?: number;
};

const clampOpts = {
  extrapolateLeft: "clamp" as const,
  extrapolateRight: "clamp" as const,
};

const BLEND_START = 0.35;
const BLEND_END = 0.65;
const BLUR_START = 0.2;
const BLUR_PEAK = 0.5;
const BLUR_END = 0.8;

const RADIAL_COPIES = 8;
const RADIAL_SPREAD = 0.2;
const RADIAL_GAUSSIAN = 0.4;

const ZoomBlurPresentation: React.FC<
  TransitionPresentationComponentProps<ZoomBlurProps>
> = ({
  children,
  presentationProgress,
  presentationDirection,
  passedProps,
}) => {
  const { blur: fullBlur = 24, rise = 0, zoom = 1.4 } = passedProps;
  const blur = useTransitionBlur(fullBlur);
  const radial = useStackedBlur();
  const entering = presentationDirection === "entering";
  const p = presentationProgress;

  if (isTransitionIdle(presentationDirection, p)) {
    return <AbsoluteFill>{children}</AbsoluteFill>;
  }

  const opacity = entering
    ? interpolate(p, [BLEND_START, BLEND_END], [0, 1], clampOpts)
    : 1;
  // The opaque entering scene covers the exiting one, unless `rise` shifts it off an edge.
  const covered = !entering && p >= BLEND_END && rise === 0;
  const intensity =
    opacity === 0 || covered
      ? 0
      : entering
        ? p < BLUR_END
          ? motionBlur((BLUR_END - p) / (BLUR_END - BLUR_PEAK))
          : 0
        : p > BLUR_START
          ? motionBlur((p - BLUR_START) / (BLUR_PEAK - BLUR_START))
          : 0;
  const gaussian = intensity * blur * (radial ? RADIAL_GAUSSIAN : 1);

  const style: React.CSSProperties = {
    opacity,
    transform: entering
      ? `scale(${interpolate(p, [BLEND_START, 1], [zoom, 1], {
          ...clampOpts,
          easing: Easing.out(Easing.cubic),
        })})${rise > 0 ? ` translateY(${(1 - p) * rise}px)` : ""}`
      : `scale(${interpolate(p, [0, BLEND_END], [1, zoom], {
          ...clampOpts,
          easing: Easing.in(Easing.cubic),
        })})${rise > 0 ? ` translateY(${-p * rise}px)` : ""}`,
    filter: intensity > 0 ? `blur(${gaussian}px)` : undefined,
  };

  return (
    <AbsoluteFill style={style}>
      {radial && intensity > 0 ? (
        <StackedBlur
          transforms={radialTransforms(
            RADIAL_SPREAD * intensity,
            RADIAL_COPIES,
          )}
        >
          {children}
        </StackedBlur>
      ) : (
        children
      )}
    </AbsoluteFill>
  );
};

function motionBlur(t: number) {
  return Math.min(t, 1) ** 2;
}

export function zoomBlur(
  props: ZoomBlurProps = {},
): TransitionPresentation<ZoomBlurProps> {
  return {
    component: ZoomBlurPresentation,
    props,
  };
}
